import React, { useState, useMemo, useEffect } from 'react';
import {
  Sparkles,
  Play,
  Square,
  Download,
  Share2,
  Radio,
  CheckCircle2,
  Coins,
} from 'lucide-react';
import { ScriptEditor } from './ScriptEditor';
import { VoiceSelector } from './VoiceSelector';
import { WaveformVisualizer } from '../common/WaveformVisualizer';
import { Voice, UseCase, AudioGeneration, JobStatus } from '../../types';
import { useAuth } from '../../context/AuthContext';

interface VoiceStudioProps {
  voices: Voice[];
  onGenerationComplete: (gen: AudioGeneration) => void;
  onToggleFavorite?: (voiceId: string) => void;
}

export const VoiceStudio: React.FC<VoiceStudioProps> = ({
  voices,
  onGenerationComplete,
  onToggleFavorite,
}) => {
  const { currentTenant } = useAuth();

  const [script, setScript] = useState(
    'For I am convinced that neither death nor life, neither angels nor demons, neither the present nor the future, nor any powers, will be able to separate us from the love of God that is in Christ Jesus our Lord. Romans 8:38-39.',
  );
  const [selectedVoice, setSelectedVoice] = useState<Voice>(voices[0] || ({} as Voice));
  const [selectedUseCase, setSelectedUseCase] = useState<UseCase>('DEVOTIONAL');

  // Ensure first available voice is selected when voices load
  useEffect(() => {
    if (voices.length > 0 && (!selectedVoice?.id || !voices.some((v) => v.id === selectedVoice.id))) {
      setSelectedVoice(voices[0]!);
    }
  }, [voices, selectedVoice]);

  // Generation Lifecycle State
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJobStatus, setCurrentJobStatus] = useState<JobStatus | null>(null);
  const [progress, setProgress] = useState(0);
  const [latestGeneration, setLatestGeneration] = useState<AudioGeneration | null>(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  // Credit calculation
  const multiplier = selectedVoice?.creditMultiplier || 1.0;
  const estimatedCreditsBurned = useMemo(() => {
    if (!script.trim()) return 0;
    return Math.max(10, Math.round(script.length * multiplier));
  }, [script, multiplier]);

  const handleGenerate = () => {
    if (!script.trim() || isGenerating) return;

    setIsGenerating(true);
    setCurrentJobStatus('QUEUED');
    setProgress(20);

    setTimeout(() => {
      setCurrentJobStatus('PROCESSING');
      setProgress(60);
    }, 800);

    setTimeout(() => {
      setProgress(90);
    }, 1600);

    setTimeout(() => {
      const newGen: AudioGeneration = {
        id: `gen_${Date.now()}`,
        tenantId: currentTenant?.id || 'ten_grace-church',
        title: script.slice(0, 48) + '...',
        script,
        voiceId: selectedVoice.id,
        voiceName: selectedVoice.name,
        categoryName: selectedVoice.categoryName || 'Standard Voices',
        creditMultiplier: multiplier,
        creditsBurned: estimatedCreditsBurned,
        provider: selectedVoice.provider,
        useCase: selectedUseCase,
        status: 'COMPLETED',
        progress: 100,
        audioUrl: 'https://actions.google.com/sounds/v1/ambiences/gentle_morning_birds.ogg',
        durationSeconds: Math.max(12, Math.round(script.split(/\s+/).length / 2.5)),
        characterCount: script.length,
        estimatedCostUsd: (script.length / 1000) * 0.15,
        scripturesDetected: ['Romans 8:38-39'],
        createdAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
      };

      setLatestGeneration(newGen);
      setCurrentJobStatus('COMPLETED');
      setIsGenerating(false);
      setProgress(100);
      onGenerationComplete(newGen);
    }, 2400);
  };

  return (
    <div className="space-y-8 w-full">
      {/* Studio Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5">
            <Radio className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            Voice Synthesis Studio
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Produce scripture recaps, devotional audio, and ministry liturgies with peace of mind.
          </p>
        </div>

        {/* Church Credit Balance Pill */}
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 shadow-sm dark:bg-slate-900 dark:border-slate-800 dark:text-slate-200 text-xs font-semibold self-start sm:self-auto">
          <Coins className="w-4 h-4 text-amber-500 dark:text-amber-400" />
          <span>{(currentTenant?.creditsBalance || 245000).toLocaleString()} Credits Available</span>
        </div>
      </div>

      {/* Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Script Editor (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-sm dark:shadow-xl space-y-4 transition-colors">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500" />
              1. Scripture Script
            </h2>
            <ScriptEditor
              script={script}
              onChange={setScript}
              selectedUseCase={selectedUseCase}
              onSelectUseCase={setSelectedUseCase}
              creditMultiplier={multiplier}
            />
          </div>
        </div>

        {/* Right Column: Voice Selection & Action (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-sm dark:shadow-xl space-y-4 transition-colors">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              2. Ministry Voice
            </h2>
            <VoiceSelector
              voices={voices}
              selectedVoiceId={selectedVoice?.id}
              onSelectVoice={setSelectedVoice}
              onToggleFavorite={onToggleFavorite}
            />
          </div>

          {/* Action Card */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/80 dark:border-slate-800 dark:shadow-xl space-y-5 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Selected Voice</div>
                <div className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                  {selectedVoice?.name || 'Select a voice'}
                </div>
                <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                  {selectedVoice?.categoryName || 'Standard'} ({multiplier.toFixed(1)}x Rate)
                </div>
              </div>

              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Estimated Burn</div>
                <div className="text-base font-bold text-amber-600 dark:text-amber-400 mt-0.5 flex items-center justify-end gap-1">
                  <Coins className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                  <span>{estimatedCreditsBurned.toLocaleString()} Credits</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={isGenerating || !script.trim()}
              className="w-full py-3.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <Sparkles className="w-4 h-4 animate-spin text-amber-300" />
                  <span>
                    {currentJobStatus === 'QUEUED'
                      ? 'Preparing Synthesis...'
                      : `Generating Audio (${progress}%)...`}
                  </span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>Synthesize Scripture Audio</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Generated Audio Player & Waveform Visualizer */}
      {latestGeneration && (
        <div className="p-6 rounded-2xl bg-white border border-indigo-200 shadow-md dark:bg-slate-900/90 dark:border-indigo-500/30 dark:shadow-2xl space-y-4 animate-in fade-in zoom-in-95 transition-colors">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <button
                onClick={() => setIsPlayingAudio(!isPlayingAudio)}
                className="w-12 h-12 rounded-xl bg-gradient-to-tr from-indigo-600 to-amber-500 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 hover:scale-105 transition-all flex-shrink-0 cursor-pointer"
              >
                {isPlayingAudio ? (
                  <Square className="w-5 h-5 fill-current" />
                ) : (
                  <Play className="w-5 h-5 fill-current ml-0.5" />
                )}
              </button>

              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900 dark:text-white text-sm">
                    {latestGeneration.title}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Ready
                  </span>
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 flex items-center gap-2">
                  <span>Voice: <strong className="text-slate-900 dark:text-slate-200">{latestGeneration.voiceName}</strong></span>
                  <span>•</span>
                  <span>{latestGeneration.durationSeconds}s audio</span>
                  <span>•</span>
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">🪙 {latestGeneration.creditsBurned.toLocaleString()} Credits</span>
                </div>
              </div>
            </div>

            {/* Actions: Download MP3 & Share */}
            <div className="flex items-center gap-2">
              <a
                href={latestGeneration.audioUrl}
                download="scripture-audio.mp3"
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white text-xs font-semibold dark:border-slate-700 transition-all shadow-sm"
              >
                <Download className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                <span>Download MP3</span>
              </a>
              <button
                onClick={() => alert('Streaming link copied to clipboard.')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700 transition-all cursor-pointer"
                title="Copy Stream URL"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Animated Waveform Visualizer */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800/80">
            <WaveformVisualizer isPlaying={isPlayingAudio} barCount={48} height={40} />
          </div>
        </div>
      )}
    </div>
  );
};
